import { useState } from "react";
import { ChevronLeft, ChevronRight, Quote, Play } from "lucide-react";

const items = [
  {
    quote:
      "The program provided clear, structured insights into real-world development practices and team workflows.",
    extra:
      "The sessions helped bridge the gap between academic learning and practical software development expectations.",
    name: "VIT University — SDC Cell",
    role: "AI Hands-on Workshop · Software Developers",
  },
  {
    quote:
      "The program strengthened communication, workplace readiness, and confidence through structured language and employability training.",
    extra:
      "Foreign language exposure combined with soft skills created a strong foundation for global career opportunities.",
    name: "MGR University — Off Campus, Arni",
    role: "Employability Program · Language + Skills",
  },
  {
    quote:
      "An end-to-end engagement built around outcomes, not deliverables. The Deployedd team embedded with us, understood the context, and delivered measurable capability uplift across functions.",
    extra: "",
    name: "ACS Group",
    role: "Capability Engagement",
  },
];

const VIDEO_URL =
  "https://customer-assets.emergentagent.com/job_deployedd-labs/artifacts/u1fctsco_Desabandhu%20feedback.mp4";

export const Testimonials = () => {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const t = items[i];
  const next = () => setI((v) => (v + 1) % items.length);
  const prev = () => setI((v) => (v - 1 + items.length) % items.length);

  return (
    <section data-testid="testimonials-section" className="container-wide py-24 md:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">
            / 04 — Voices
          </p>
          <h2 className="font-heading text-4xl md:text-5xl tracking-tighter leading-[0.95]">
            Client stories,<br />
            <em className="font-editorial italic text-[#9F381A]">in their words.</em>
          </h2>
        </div>
        <div className="lg:col-span-8">
          <div className="relative border-l-2 border-[#9F381A] pl-8 md:pl-12">
            <Quote className="absolute -left-3 top-0 w-6 h-6 bg-[#FAFAFA] text-[#9F381A]" />
            <p data-testid="testimonial-quote" className="font-editorial text-2xl md:text-3xl lg:text-4xl leading-snug italic text-ink mb-6">
              "{t.quote}"
            </p>
            {t.extra && (
              <p data-testid="testimonial-quote-extra" className="font-editorial text-xl md:text-2xl leading-snug italic text-ink-muted mb-10">
                "{t.extra}"
              </p>
            )}
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p data-testid="testimonial-name" className="font-heading text-lg font-medium">{t.name}</p>
                <p className="text-sm text-gray-500 uppercase tracking-[0.2em]">{t.role}</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="testimonial-prev" onClick={prev} className="w-11 h-11 border border-ink flex items-center justify-center hover:bg-ink hover:text-white transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button data-testid="testimonial-next" onClick={next} className="w-11 h-11 border border-ink flex items-center justify-center hover:bg-ink hover:text-white transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <span className="font-mono-cust text-xs text-gray-500 ml-3">
                  {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video testimonial — landscape 16:9 frame, portrait video letterboxed */}
      <div data-testid="video-testimonial" className="mt-24 md:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#9F381A] mb-6">
            / On camera — Client feedback
          </p>
          <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[0.95] mb-6">
            A first-hand account of working with <em className="font-editorial italic text-[#9F381A]">Deployedd Labs.</em>
          </h3>
          <p className="text-lg text-ink-muted leading-relaxed">
            Engagement experience in their own words — capability building, deployment, and the operating rhythm that makes Deployedd different.
          </p>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="relative w-full aspect-video bg-ink overflow-hidden group">
            {!playing ? (
              <button
                data-testid="video-play-btn"
                onClick={() => setPlaying(true)}
                className="absolute inset-0 w-full h-full flex items-center justify-center"
                aria-label="Play testimonial video"
              >
                <video
                  src={VIDEO_URL}
                  className="absolute inset-0 w-full h-full object-contain opacity-70 group-hover:opacity-90 transition-opacity bg-ink"
                  muted
                  preload="none"
                />
                <span className="absolute inset-0 bg-ink/30 group-hover:bg-ink/15 transition-colors" />
                <span className="relative z-10 w-20 h-20 md:w-24 md:h-24 rounded-full bg-white flex items-center justify-center group-hover:bg-[#9F381A] group-hover:text-white transition-colors">
                  <Play className="w-7 h-7 ml-1 fill-current" />
                </span>
                <span className="absolute bottom-5 left-5 font-mono-cust text-[11px] uppercase tracking-[0.25em] text-white">
                  Watch · Client feedback
                </span>
              </button>
            ) : (
              <video
                data-testid="video-testimonial-player"
                src={VIDEO_URL}
                className="w-full h-full object-contain bg-ink"
                controls
                autoPlay
                playsInline
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
